// Copyright © 2022 Niphtio, Inc.
// All Rights Reserved.

import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import throttle from 'lodash/throttle';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { copy } from '~/common/copy/copy';
import { cookieStore } from '~/common/utilities/cookie-utils/cookieStore';
import { localStore } from '~/common/utilities/local-store/localStore';
import { getStoredReferrer } from '~/common/utilities/tracking-utils/referrer/store-referrer';
import { getIsSomeUTM } from '~/common/utilities/tracking-utils/utm/get-is-some-utm';
import { getUTMFromWindow } from '~/common/utilities/tracking-utils/utm/get-utm-from-window';
import { getStoredUTM } from '~/common/utilities/tracking-utils/utm/store-utm';
import { TranslationProtection } from '~/components/TranslationProtection/TranslationProtection';
import { StaticLayout } from '~/containers/layouts/StaticLayout';
import { useRequestAccountMutationHelper } from '~/hooks/useRequestAccountMutationHelper';
import { useToastHelper } from '~/hooks/useToastHelper';

export const SignupPage: FC = () => {
  const { isOpen, onOpen } = useDisclosure();
  const { renderToastWith } = useToastHelper();

  const { register, handleSubmit, formState, getValues } = useForm({
    mode: 'onChange',
  });

  const { isValid, isSubmitted, errors } = formState;

  const { email: emailError, name: nameError } = errors;

  const [requestAccount, { loading }] = useRequestAccountMutationHelper();

  const onSubmit = throttle(({ email }) => {
    const storedUTMLocalStore = getStoredUTM(localStore);
    const storedUTMCookieStore = getStoredUTM(cookieStore);
    const storedUTMCurrentWindow = getUTMFromWindow(window);
    // if any utm parameters are present, use that storage as the utm source
    // for utm, trust current window the most, then cookie store, then local store
    const storedUTM = getIsSomeUTM(storedUTMCurrentWindow)
      ? storedUTMCurrentWindow
      : getIsSomeUTM(storedUTMCookieStore)
        ? storedUTMCookieStore
        : storedUTMLocalStore;
    // for referrer, trust the cookie store the most, then local store, then window referrer
    const storedReferrerCookieStore = getStoredReferrer(cookieStore);
    const storedReferrerLocalStore = getStoredReferrer(localStore);
    const referrer =
      storedReferrerCookieStore ??
      storedReferrerLocalStore ??
      window.document?.referrer;
    const utm_campaign = storedUTM?.utmCampaign;
    const utm_content = storedUTM?.utmContent;
    const utm_medium = storedUTM?.utmMedium;
    const utm_source = storedUTM?.utmSource;
    const utm_term = storedUTM?.utmTerm;

    requestAccount(
      {
        input: {
          email,
          referrer,
          utm_campaign,
          utm_content,
          utm_medium,
          utm_source,
          utm_term,
        },
      },
      {
        onSuccess: () => {
          onOpen();
          try {
            gtag('event', 'sign_up', {
              method: 'Webapp',
              value: 'Sign up',
              event_category: 'Sign up',
              event_label: 'Sign up',
            });
          } catch (e) {
            console.log(e);
          }
        },
      },
    );
  }, 5000);

  const resend = (e) => {
    renderToastWith({
      duration: 5000,
      message: copy.confirmResent,
    })();

    handleSubmit(onSubmit)(e);
  };

  return (
    <StaticLayout split={false} footer={true} hasTagline={false}>
      {isOpen && (
        <Stack w={['xs', 'sm']} spacing={6}>
          <Heading>Please check your inbox</Heading>
          <Box>
            We’ve sent a verification email to{' '}
            <Text
              display="inline-block"
              fontWeight="bold"
              data-dd-privacy="mask"
            >
              {getValues('email')}.
            </Text>{' '}
            If you don’t receive the email after a few minutes,{' '}
            <Link variant="npAccent" onClick={resend}>
              click here to resend it
            </Link>
            .
          </Box>
        </Stack>
      )}

      {!isOpen && (
        <Flex
          flexDir={['column', 'column', 'column', 'row']}
          maxW="4xl"
          zIndex={11}
          mt={12}
        >
          <Card maxW={['xs', 'sm', 'md']} p={[2, 9, 12]} borderRadius="md">
            <CardBody>
              <Stack as="form" spacing={6} onSubmit={handleSubmit(onSubmit)}>
                <Heading fontSize="3xl">Create your account</Heading>
                <FormControl id="fc-email" isInvalid={!!emailError}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    variant="npOutline"
                    id="email"
                    type="email"
                    aria-label="email"
                    name="email"
                    autoComplete="email"
                    isRequired
                    isDisabled={loading}
                    autoFocus
                    {...register('email', {
                      required: true,
                      pattern: /\S+@\S+/,
                    })}
                  />
                </FormControl>
                <Text fontSize="sm">
                  We’ll send you a secure link to create your password. We won’t
                  share or sell your personal data.
                </Text>
                <Button variant="npPrimary" type="submit" isLoading={loading}>
                  <TranslationProtection>
                    Send me the link
                  </TranslationProtection>
                </Button>
                <Link textAlign="center" variant="npAccent" href="/login">
                  I already have an account
                </Link>
                <Box minH={'25px' /* Reduces layout shift */}>
                  <FormControl isInvalid={isSubmitted && !isValid}>
                    <FormErrorMessage>
                      Please enter a valid email and password.
                    </FormErrorMessage>
                  </FormControl>
                </Box>
              </Stack>
            </CardBody>
          </Card>

          <Stack
            my={[16, 16, 16, 0]}
            pl={[0, 0, 16, 16]}
            direction="column"
            w={['xs', 'sm']}
            mx="auto"
          >
            <Heading size="md" pb={4} w="15ch" color="npGray.700">
              Why Niphtio?
            </Heading>

            <Stack gap={12} direction="column">
              <Stack direction="row" alignItems="center">
                <Box pr={5}>
                  <Image
                    minWidth="40px"
                    src="/signup/icon-smile.svg"
                    alt="smile"
                  ></Image>
                </Box>
                <Text>
                  No ads, no tracking cookies, and we will never sell your data.
                </Text>
              </Stack>

              <Stack direction="row" alignItems="center">
                <Box pr={5}>
                  <Image
                    minWidth="40px"
                    src="/signup/icon-bookmark.svg"
                    alt="bookmark"
                  ></Image>
                </Box>
                <Text>Save any web page with just one click.</Text>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Box pr={5}>
                  <Image
                    minWidth="40px"
                    src="/signup/icon-note.svg"
                    alt="note"
                  ></Image>
                </Box>
                <Text>
                  Add notes or tags to remember why you saved an article.
                </Text>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Box pr={5}>
                  <Image
                    minWidth="40px"
                    src="/signup/icon-search.svg"
                    alt="search"
                  ></Image>
                </Box>
                <Text>Quickly find saved items again with search.</Text>
              </Stack>
            </Stack>
          </Stack>
        </Flex>
      )}
    </StaticLayout>
  );
};
